import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Printer } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileTemplateViewer } from '@/components/MobileTemplateViewer';
import { escapeHtml, sanitizeUrl, safeDisplayText } from '@/lib/utils';

interface ChecklistItem {
  id: string;
  target: boolean;
  ready: boolean;
  insert: boolean;
  item: string;
  comment: string;
  url?: string;
}

interface SampleChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  phaseId: string;
  phaseTitle: string;
  sectionName?: string;
  isSampleChecklist?: boolean;
  isSharedChecklist?: boolean;
  preloadedItems?: ChecklistItem[];
}

const renderNotes = (notes: string) => {
  if (!notes) return null;
  
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = notes.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      const sanitizedUrl = sanitizeUrl(part);
      return (
        <a
          key={index}
          href={sanitizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary-dark underline"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export const SampleChecklistModal = ({ isOpen, onClose, phaseId, phaseTitle, sectionName = "Sample Checklist", isSampleChecklist = false, isSharedChecklist = false, preloadedItems = [] }: SampleChecklistModalProps) => {
  const [items, setItems] = useState<ChecklistItem[]>(preloadedItems);
  const [mobileViewer, setMobileViewer] = useState<{isOpen: boolean, item: ChecklistItem | null}>({isOpen: false, item: null});
  const isMobile = useIsMobile();

  const updateItem = (id: string, field: keyof ChecklistItem, value: string | boolean) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Update items when preloadedItems change
  useEffect(() => {
    setItems(preloadedItems);
  }, [preloadedItems]);

  const handleMobileItemClick = (item: ChecklistItem) => {
    if (isMobile && isSampleChecklist) {
      setMobileViewer({isOpen: true, item});
    }
  };

  const printChecklist = () => {
    // Store original title to restore later
    const originalTitle = document.title;
    document.title = `${sectionName} - ${phaseTitle} Checklist`;
    
    // Create print container
    const printContainer = document.createElement('div');
    printContainer.className = 'print-container';
    printContainer.style.visibility = 'hidden';
    printContainer.style.position = 'absolute';
    printContainer.style.height = '0';
    printContainer.style.overflow = 'hidden';
    
    // Build rows with padding to minimum 12 rows
    const allRows = [...items];
    while (allRows.length < 12) {
      allRows.push({
        id: `blank-${allRows.length}`,
        target: false,
        ready: false,
        insert: false,
        item: '',
        comment: ''
      });
    }
    
    // Build print content
    printContainer.innerHTML = `
      <h1>Sink Bike Run<br/>Checklist</h1>
      <h2>${sectionName}</h2>
      <h2>${phaseTitle}</h2>
      <table>
        <thead>
          <tr>
             <th style="width: 40px;">S</th>
             <th style="width: 40px;">B</th>
             <th style="width: 40px;">R</th>
            <th style="width: 35%;">Item</th>
            <th style="width: 35%;">Notes</th>
            <th style="width: 100px; text-align: center;">Ads</th>
          </tr>
        </thead>
        <tbody>
          ${allRows.map((item, idx) => `
            <tr>
              <td><input type="checkbox" name="target-${idx}" class="checkbox" /></td>
              <td><input type="checkbox" name="ready-${idx}" class="checkbox" /></td>
              <td><input type="checkbox" name="insert-${idx}" class="checkbox" /></td>
              <td style="font-weight: 400;">${safeDisplayText(item.item)}</td>
              <td>${safeDisplayText(item.comment)}</td>
              <td style="text-align: center;">${item.url ? 'Shop' : ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    
    // Add to DOM
    document.body.appendChild(printContainer);
    
    // Trigger print with delay for iOS Safari
    setTimeout(() => {
      window.print();
    }, 150);
    
    // Clean up after print dialog closes
    const cleanupPrint = () => {
      document.title = originalTitle;
      document.body.removeChild(printContainer);
      window.removeEventListener('afterprint', cleanupPrint);
    };
    
    window.addEventListener('afterprint', cleanupPrint);
    
    // Fallback cleanup after 1 second in case afterprint doesn't fire
    setTimeout(() => {
      if (document.body.contains(printContainer)) {
        document.title = originalTitle;
        document.body.removeChild(printContainer);
      }
    }, 1000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] md:max-h-[90vh] md:min-h-[600px] flex flex-col">
        <DialogHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
            <div className="flex flex-col gap-1 md:gap-2">
              {/* Desktop layout */}
              <div className="hidden md:flex flex-row items-center gap-2">
                <span className="text-xl font-normal text-foreground">{sectionName}</span>
                <span className="text-muted-foreground text-xl">-</span>
                <span className="text-xl font-normal text-foreground">{phaseTitle}</span>
              </div>
              
              {/* Mobile layout - Single row with left-justified title */}
              <div className="md:hidden flex items-center justify-start mt-8">
                <span className="text-sm font-normal text-foreground truncate">
                  {sectionName} - {phaseTitle}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No items found for this phase. Check with admin to add content.
            </div>
          ) : (
            isSampleChecklist ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">S</TableHead>
                      <TableHead className="w-12">B</TableHead>
                      <TableHead className="w-12">R</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Notes</TableHead>
                      {!isSharedChecklist && <TableHead className="w-20">Ads</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="p-2">
                          <div className="flex items-center justify-center">
                            <Checkbox checked={item.target} disabled className="cursor-not-allowed h-4 w-4" />
                          </div>
                        </TableCell>
                        <TableCell className="p-2">
                          <div className="flex items-center justify-center">
                            <Checkbox checked={item.ready} disabled className="cursor-not-allowed h-4 w-4" />
                          </div>
                        </TableCell>
                        <TableCell className="p-2">
                          <div className="flex items-center justify-center">
                            <Checkbox checked={item.insert} disabled className="cursor-not-allowed h-4 w-4" />
                          </div>
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={item.item}
                            onClick={isMobile ? () => handleMobileItemClick(item) : undefined}
                            readOnly
                            tabIndex={-1}
                            onFocus={(e) => e.target.blur()}
                            className={`w-full border-none shadow-none p-0 h-auto ${isMobile ? 'cursor-pointer' : 'cursor-default'} focus:ring-0 focus:ring-offset-0`}
                            aria-label="Item description"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={item.comment}
                            onClick={isMobile ? () => handleMobileItemClick(item) : undefined}
                            readOnly
                            tabIndex={-1}
                            onFocus={(e) => e.target.blur()}
                            className={`w-full border-none shadow-none p-0 h-auto ${isMobile ? 'cursor-pointer' : 'cursor-default'} focus:ring-0 focus:ring-offset-0`}
                            aria-label="Notes for this item"
                          />
                        </TableCell>
                        {!isSharedChecklist && (
                          <TableCell className="p-2 text-center">
                            {item.url && (
                              <Button 
                                size="sm" 
                                variant="accent"
                                className="h-6 px-2 text-xs"
                                onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                              >
                                Shop
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.id} className="border rounded-lg p-4 bg-card">
                    <div className="flex items-start gap-4">
                       <div className="flex gap-2">
                         <Checkbox
                           checked={item.target}
                           disabled
                           className="mt-1"
                         />
                         <Checkbox
                           checked={item.ready}
                           disabled
                           className="mt-1"
                         />
                         <Checkbox
                           checked={item.insert}
                           disabled
                           className="mt-1"
                         />
                       </div>
                       <div className="flex-1">
                          <div className="cursor-not-allowed">{item.item}</div>
                          {item.comment && (
                            <div className="mt-1">
                              {renderNotes(item.comment)}
                            </div>
                          )}
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          
          {/* Add explanation text for S, B, R */}
          {items.length > 0 && (
            <div className="bg-muted/20 p-3 rounded-lg text-sm mt-4">
              <div className="grid grid-cols-3 gap-4 text-muted-foreground text-xs font-normal">
                <div><span className="font-normal">Secured (S):</span> Check this box when you possess the item.</div>
                <div><span className="font-normal">Bundled (B):</span> Check this box when you have staged the item for packing.</div>
                <div><span className="font-normal">Ready (R):</span> Check this box when you've physically packed the item.</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Template Viewer */}
        <MobileTemplateViewer 
          isOpen={mobileViewer.isOpen}
          onClose={() => setMobileViewer({isOpen: false, item: null})}
          item={mobileViewer.item}
          hideCommercialElements={isSharedChecklist}
        />
      </DialogContent>
    </Dialog>
  );
};